import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { categories, subcategories, getMoviesByCategory, Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSub, setActiveSub] = useState<string | null>(null);
  const [movies, setMovies] = useState<Movie[]>([]);
  const [sortBy, setSortBy] = useState<'default' | 'rating' | 'year'>('default');

  const category = categories.find((c) => c.slug === slug);
  const subs = slug ? (subcategories[slug] || []) : [];

  useEffect(() => {
    if (slug) {
      const allMovies = getMoviesByCategory(slug);
      setMovies(allMovies);
      setActiveSub(null);
    }
  }, [slug]);

  const filteredMovies = (() => {
    let result = activeSub ? movies.filter((m) => m.subcategory === activeSub) : [...movies];
    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result.sort((a, b) => b.year - a.year);
    }
    return result;
  })();

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <p className="text-foreground-500 text-lg">分类不存在</p>
            <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryNameMap: Record<string, string> = {
    movie: '电影',
    tv: '电视剧',
    anime: '动漫',
    variety: '综艺',
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="min-h-screen pt-20 bg-background-50">
        <Breadcrumb
          items={[
            { label: categoryNameMap[category.slug] || category.name },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-8 gap-4">
              <div>
                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 font-bold mb-1">
                  {category.name}
                  <span className="text-base text-foreground-500 font-normal ml-2">
                    共{filteredMovies.length}部
                  </span>
                </h1>
                <p className="text-sm text-foreground-500">
                  高清蓝光画质，极速播放不卡顿
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground-500">排序:</span>
                {[
                  { key: 'default' as const, label: '默认' },
                  { key: 'rating' as const, label: '评分最高' },
                  { key: 'year' as const, label: '最新发布' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    onClick={() => setSortBy(opt.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === opt.key
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {subs.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <button
                  onClick={() => setActiveSub(null)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeSub === null
                      ? 'bg-accent-500 text-foreground-950'
                      : 'bg-background-100 text-foreground-600 hover:text-foreground-950 border border-background-200/70'
                  }`}
                >
                  全部
                </button>
                {subs.map((sub) => (
                  <button
                    key={sub}
                    onClick={() => setActiveSub(sub)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      activeSub === sub
                        ? 'bg-accent-500 text-foreground-950'
                        : 'bg-background-100 text-foreground-600 hover:text-foreground-950 border border-background-200/70'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </div>
            )}

            {filteredMovies.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {filteredMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-foreground-500">暂无该类别的影片</p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 80s电影网`,
            description: `80s电影网${category.name}分类，高清在线看免费不卡。涵盖各类${category.name}资源，蓝光画质极速播放。`,
            url: `https://www.80sdyw.com/category/${slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '80s电影网',
              url: 'https://www.80sdyw.com/',
            },
          }),
        }}
      />
    </>
  );
}